import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Loader2 } from 'lucide-react';
import { getAllTarifs } from '../../../services/tarifsService';

/**
 * La grille des formules de la tarification d'origine.
 *
 * ── Une copie, classe pour classe ───────────────────────────────────────────
 * Le JSX est celui de PricingPage : bascule mensuel/annuel, grille d'autant
 * de colonnes que de formules, carte « populaire » en dégradé légèrement
 * agrandie, prix brut suivi de sa période, économie annuelle, bouton pleine
 * largeur, note en italique. Seuls les textes changent de source : ils
 * viennent du bloc — donc de la base, traduits langue par langue — au lieu
 * des fichiers i18n.
 *
 * ── Les formules restent lues en base ───────────────────────────────────────
 * Comme le bloc `tarif`, aucun prix n'est recopié dans le bloc : un tarif
 * modifié en base se verrait faux ici. `getAllTarifs` est l'appel de la page
 * d'origine, qui écarte déjà la formule BETA.
 *
 * ── On ne souscrit pas depuis cette page ────────────────────────────────────
 * Elle est informative : le choix d'une formule se fait à la fin de
 * l'inscription. Le bouton mène donc au même endroit sur toutes les cartes.
 */
const lireCode = (tarif) => String(tarif.code ?? tarif.CODE ?? '').toUpperCase();
const lireNombre = (tarif, ...cles) => {
    for (const cle of cles) {
        if (tarif[cle] !== undefined && tarif[cle] !== null) return parseFloat(tarif[cle]) || 0;
    }
    return 0;
};

const GrilleTarifsBloc = ({ bloc, langue = 'fr' }) => {
    const [tarifs, setTarifs] = useState([]);
    const [chargement, setChargement] = useState(true);
    const [periode, setPeriode] = useState('monthly');

    useEffect(() => {
        let annule = false;

        getAllTarifs()
            .then(data => { if (!annule && Array.isArray(data)) setTarifs(data); })
            .catch(() => { /* la grille reste vide : voir TarifBloc */ })
            .finally(() => { if (!annule) setChargement(false); });

        return () => { annule = true; };
    }, []);

    const codePopulaire = String(bloc.codePopulaire || '').trim().toUpperCase();
    const parAn = bloc.parAn || '/an';
    const parMois = bloc.parMois || '/mois';
    // « Économisez 20€/an » : le suffixe sans sa barre, comme la page d'origine.
    const uniteAn = parAn.replace(/^\s*\//, '');

    return (
        <section data-testid="bloc-grille-tarifs">
            {/* Bascule mensuel / annuel */}
            <div className="flex items-center justify-center gap-2 mb-8">
                <div className="bg-gray-100 p-1 rounded-xl flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => setPeriode('monthly')}
                        className={`px-5 py-2.5 min-h-11 sm:min-h-0 rounded-lg text-sm font-medium transition-all ${
                            periode === 'monthly'
                                ? 'bg-white text-cabaza-purple shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        {bloc.libelleMensuel || 'Mensuel'}
                    </button>
                    <button
                        type="button"
                        onClick={() => setPeriode('yearly')}
                        className={`px-5 py-2.5 min-h-11 sm:min-h-0 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                            periode === 'yearly'
                                ? 'bg-white text-cabaza-purple shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        {bloc.libelleAnnuel || 'Annuel'}
                        {bloc.remise && (
                            <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-semibold">
                                {bloc.remise}
                            </span>
                        )}
                    </button>
                </div>
            </div>

            {/* Cartes des formules */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
                {chargement ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 text-cabaza-purple animate-spin" />
                    </div>
                ) : (
                    <div className={`grid grid-cols-1 gap-8 ${
                        tarifs.length === 4 ? 'md:grid-cols-4' :
                        tarifs.length === 3 ? 'md:grid-cols-3' :
                        'md:grid-cols-2'
                    }`}>
                        {tarifs.map((tarif) => {
                            const code = lireCode(tarif);
                            const populaire = Boolean(codePopulaire) && code === codePopulaire;
                            const prixMois = lireNombre(tarif, 'monthlyprice', 'monthlyPrice', 'MONTHLYPRICE');
                            const prixAn = lireNombre(tarif, 'yearlyprice', 'yearlyPrice', 'YEARLYPRICE');
                            const prix = periode === 'yearly' ? prixAn : prixMois;
                            const libellePeriode = periode === 'yearly' ? parAn : parMois;
                            const nom = tarif[`name_${langue}`] || tarif.name_fr || tarif.NAME_FR || tarif.name || code;
                            const description = tarif[`description_${langue}`] || tarif.description_fr || tarif.DESCRIPTION_FR || '';

                            return (
                                <div
                                    key={code}
                                    className={`relative rounded-2xl p-8 ${
                                        populaire
                                            ? 'bg-linear-to-br from-cabaza-purple to-cabaza-pink text-white shadow-2xl scale-105 z-10'
                                            : 'bg-white border-2 border-gray-100 shadow-lg hover:shadow-xl hover:border-cabaza-purple/20'
                                    } transition-all duration-300`}
                                >
                                    {populaire && bloc.libellePopulaire && (
                                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                            <span className="inline-flex items-center gap-1 bg-white text-cabaza-purple px-4 py-1 rounded-full text-sm font-semibold shadow-lg">
                                                <Sparkles className="w-4 h-4" />
                                                {bloc.libellePopulaire}
                                            </span>
                                        </div>
                                    )}

                                    <h3 className={`text-2xl font-bold mb-2 ${populaire ? 'text-white' : 'text-gray-900'}`}>
                                        {nom}
                                    </h3>
                                    <p className={`text-sm mb-6 ${populaire ? 'text-white/80' : 'text-gray-500'}`}>
                                        {description}
                                    </p>

                                    <div className="mb-8">
                                        <span className={`text-5xl font-bold ${populaire ? 'text-white' : 'text-gray-900'}`}>
                                            {prix}€
                                        </span>
                                        <span className={`text-lg ${populaire ? 'text-white/80' : 'text-gray-500'}`}>
                                            {libellePeriode}
                                        </span>
                                        {periode === 'yearly' && prixMois > 0 && (
                                            <div className={`mt-1 text-xs ${populaire ? 'text-white/70' : 'text-green-600'}`}>
                                                {bloc.economisez || 'Économisez'} {Math.round(prixMois * 12 - prixAn)}€/{uniteAn}
                                            </div>
                                        )}
                                    </div>

                                    {bloc.boutonTexte && (
                                        <Link
                                            to={bloc.boutonLien || '/register'}
                                            className={`block w-full py-3 px-6 rounded-xl font-semibold text-center transition-all duration-200 ${
                                                populaire
                                                    ? 'bg-white text-cabaza-purple hover:bg-gray-100 hover:shadow-lg'
                                                    : 'bg-linear-to-r from-cabaza-purple to-cabaza-pink text-white hover:shadow-lg hover:scale-105'
                                            }`}
                                        >
                                            {bloc.boutonTexte}
                                        </Link>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {bloc.note && (
                    <div className="mt-8 text-center">
                        <p className="text-sm text-gray-500 italic">{bloc.note}</p>
                    </div>
                )}
            </div>
        </section>
    );
};

export default GrilleTarifsBloc;
